import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import pluralize from 'pluralize';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { DIAM_GRID_PAGE_SIZE_OPTIONS } from '../../diamGridUtils';
import { useDiamGridPagination } from '../../hooks/useDiamGridPagination';
import { DiamDataGrid } from '../DiamDataGrid/DiamDataGrid';
import { DataGridContainer } from '../DiamDataGrid/styles';
import { InviteManuallyModal } from '../InviteManuallyModal/InviteManuallyModal';
import { Confirm } from '../../../userManagement/components/Confirm';
import { buildInvitationColumns } from './columns';
import { mapApiInvitation } from './mapApiInvitation';
import { Invitation } from './types';
import { useInvitationLink } from './useInvitationLink';
import { useInvitations } from './useInvitations';
import { useResendInvitation } from './useResendInvitation';
import { useRevokeInvitation } from './useRevokeInvitation';

export interface InvitationsGridProps {
  onInvitationsChange?: (invitations: Invitation[]) => void;
}

export function InvitationsGrid({ onInvitationsChange }: InvitationsGridProps) {
  const { enqueueSnackbar } = useSnackbar();
  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    sortModel,
    handleServerFilterModelChange,
    handlePagination,
    handleSortModelChange,
  } = useDiamGridPagination({
    initialSortField: 'status',
    initialSortOrder: 'asc',
  });

  const invitationsQuery = useInvitations({
    page: pageIndex,
    pageSize,
    sortModel,
    filterModel,
  });

  const invitationLink = useInvitationLink();
  const resendInvitation = useResendInvitation();
  const revokeInvitation = useRevokeInvitation();
  const [inviteManuallyInvitation, setInviteManuallyInvitation] =
    useState<Invitation | null>(null);
  const [revokingInvitation, setRevokingInvitation] =
    useState<Invitation | null>(null);

  const invitations = useMemo(
    () => (invitationsQuery.data?.elements ?? []).map(mapApiInvitation),
    [invitationsQuery.data],
  );

  useEffect(() => {
    onInvitationsChange?.(invitations);
  }, [invitations, onInvitationsChange]);

  useEffect(() => {
    if (invitationsQuery.isError) {
      enqueueSnackbar(
        getApiErrorMessage(
          invitationsQuery.error,
          'Failed to load invitations',
        ),
        { variant: 'error' },
      );
    }
  }, [enqueueSnackbar, invitationsQuery.error, invitationsQuery.isError]);

  const handleInviteManually = useCallback(
    async (invitation: Invitation) => {
      try {
        const result = await invitationLink.mutateAsync(invitation.id);
        setInviteManuallyInvitation({ ...invitation, link: result.link });
      } catch (error) {
        enqueueSnackbar(
          getApiErrorMessage(error, 'Failed to load invitation link'),
          { variant: 'error' },
        );
      }
    },
    [enqueueSnackbar, invitationLink],
  );

  const handleCloseInviteManually = useCallback(() => {
    setInviteManuallyInvitation(null);
  }, []);

  const handleRequestRevoke = useCallback((invitation: Invitation) => {
    setRevokingInvitation(invitation);
  }, []);

  const handleCloseRevoke = useCallback(() => {
    setRevokingInvitation(null);
  }, []);

  const handleResend = useCallback(
    async (invitation: Invitation) => {
      try {
        await resendInvitation.mutateAsync(invitation.id);
        enqueueSnackbar(`Invitation resent to ${invitation.email}`, {
          variant: 'success',
        });
      } catch (error) {
        enqueueSnackbar(
          getApiErrorMessage(error, 'Failed to resend invitation'),
          { variant: 'error' },
        );
      }
    },
    [enqueueSnackbar, resendInvitation],
  );

  const handleConfirmRevoke = useCallback(async () => {
    if (!revokingInvitation) return;

    try {
      await revokeInvitation.mutateAsync(revokingInvitation.id);
      enqueueSnackbar(`Revoked invitation for ${revokingInvitation.email}`, {
        variant: 'success',
      });
      setRevokingInvitation(null);
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(error, 'Failed to revoke invitation'),
        { variant: 'error' },
      );
    }
  }, [enqueueSnackbar, revokeInvitation, revokingInvitation]);

  const columns = useMemo(
    () =>
      buildInvitationColumns({
        onInviteManually: handleInviteManually,
        onResend: handleResend,
        onRevoke: handleRequestRevoke,
      }),
    [handleInviteManually, handleRequestRevoke, handleResend],
  );

  const totalRows = invitationsQuery.data?.total ?? 0;
  const title = `${totalRows} ${pluralize('Invitation', totalRows)}`;

  return (
    <DataGridContainer elevation={1}>
      <DiamDataGrid
        rows={invitations}
        columns={columns as GridColDef[]}
        loading={invitationsQuery.isPending || invitationsQuery.isFetching}
        title={title}
        disableRowSelectionOnClick
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleServerFilterModelChange}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePagination}
        rowCount={totalRows}
        pageSizeOptions={[...DIAM_GRID_PAGE_SIZE_OPTIONS]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        searchPlaceholder="Search by email"
      />
      {inviteManuallyInvitation && (
        <InviteManuallyModal
          isOpen
          magicLink={inviteManuallyInvitation.link}
          onClose={handleCloseInviteManually}
        />
      )}
      <Confirm
        isOpen={Boolean(revokingInvitation)}
        title="Revoke invitation?"
        content="The magic link will expire and can no longer be used."
        confirm="Revoke"
        cancel="Cancel"
        onConfirm={handleConfirmRevoke}
        onClose={handleCloseRevoke}
      />
    </DataGridContainer>
  );
}
